import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { seoSearchPerformance } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Loading, Empty, DataTable, delta, num, pct } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/seo/performance")({
  head: () => ({ meta: [{ title: "Search performance · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: SeoPerformance,
});

const RANGES = [7, 28, 90];

function SeoPerformance() {
  const load = useServerFn(seoSearchPerformance);
  const [days, setDays] = useState(28);
  const q = useQuery({ queryKey: ["seo-performance", days], queryFn: () => load({ data: { days } }) });
  const d = q.data as any;

  return (
    <SeoPage
      title="Search performance"
      description="Clicks, impressions, click-through rate and average position from Google Search Console, compared with the previous period."
      actions={RANGES.map((r) => (
        <Button key={r} size="sm" variant={r === days ? "default" : "outline"} onClick={() => setDays(r)}>
          Last {r} days
        </Button>
      ))}
    >
      {q.isLoading ? (
        <Loading />
      ) : !d?.hasData ? (
        <Empty>
          No Search Console data has been collected yet. Connect Search Console on the Integrations screen and fetch the
          latest data — this page fills in automatically once real rows arrive.
        </Empty>
      ) : (
        <>
          <p className="mb-3 text-xs text-muted-foreground">
            {d.range.start} → {d.range.end} vs {d.range.previousStart} → {d.range.previousEnd}
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Clicks" value={num(d.totals.clicks)} sub={`was ${num(d.previousTotals.clicks)}`} />
            <Stat label="Impressions" value={num(d.totals.impressions)} sub={`was ${num(d.previousTotals.impressions)}`} />
            <Stat label="Click-through rate" value={pct(d.totals.ctr)} sub={`was ${pct(d.previousTotals.ctr)}`} />
            <Stat label="Average position" value={d.totals.position.toFixed(1)} sub={`was ${d.previousTotals.position.toFixed(1)}`} />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <Panel title="Biggest gains">
              <DataTable
                head={["Page", "Clicks", "Change", "Position"]}
                rows={(d.winners ?? []).map((p: any) => [p.key, num(p.clicks), delta(p.clicksDelta), p.position.toFixed(1)])}
              />
            </Panel>
            <Panel title="Biggest drops">
              <DataTable
                head={["Page", "Clicks", "Change", "Position"]}
                rows={(d.losers ?? []).map((p: any) => [p.key, num(p.clicks), delta(p.clicksDelta), p.position.toFixed(1)])}
              />
            </Panel>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Top searches">
              <DataTable
                head={["Search", "Clicks", "Impressions", "CTR", "Position"]}
                rows={(d.topQueries ?? []).slice(0, 30).map((r: any) => [
                  r.key,
                  num(r.clicks),
                  num(r.impressions),
                  pct(r.ctr),
                  r.position.toFixed(1),
                ])}
              />
            </Panel>
            <Panel title="Top pages">
              <DataTable
                head={["Page", "Clicks", "Impressions", "CTR", "Position"]}
                rows={(d.topPages ?? []).slice(0, 30).map((r: any) => [
                  r.key,
                  num(r.clicks),
                  num(r.impressions),
                  pct(r.ctr),
                  r.position.toFixed(1),
                ])}
              />
            </Panel>
          </div>
        </>
      )}
    </SeoPage>
  );
}
